import Pusher from 'pusher-js'
import { remove } from 'lodash'

const SUBSCRIPTION_RETRY_MS = 1000

type PendingBind = { channelName: string; event: string; callback: Function }

export class PusherWrapper {
  appKey: string
  cluster: string
  environment: string | undefined = process.env.RAILS_ENV
  channelEndpoint: string = '/pusher'
  pendingChannels: string[] = []
  pendingBinds: PendingBind[] = []
  channels: string[] = []
  pusher: Pusher

  constructor(appKey: string, cluster: string) {
    this.appKey = appKey
    this.cluster = cluster
    this.init()
  }

  subscribeForEvent(channelName: string, event: string, callback: Function) {
    if (this.channels.includes(channelName)) {
      this.dropPendingBind(channelName, event, callback)
      const channel = this.pusher.channel(channelName)
      channel.bind(event, callback)
      return
    }

    if (!this.hasPendingBind(channelName, event, callback)) {
      this.pendingBinds.push({ channelName, event, callback })
    }

    if (this.pendingChannels.includes(channelName)) {
      setTimeout(() => {
        if (!this.hasPendingBind(channelName, event, callback)) return

        this.subscribeForEvent(channelName, event, callback)
      }, SUBSCRIPTION_RETRY_MS)
      return
    }

    this.pendingChannels.push(channelName)
    const channel = this.pusher.subscribe(channelName)

    channel.bind('pusher:subscription_succeeded', () => {
      this.channels.push(channelName)
      remove(this.pendingChannels, (pendingChannel) => pendingChannel === channelName)

      if (!this.hasPendingBind(channelName, event, callback)) return

      this.dropPendingBind(channelName, event, callback)
      channel.bind(event, callback)
    })
  }

  unsubscribeFromTheChannel(channelName: string) {
    this.pusher.unsubscribe(channelName)
    remove(this.channels, (channel) => channel === channelName)
  }

  // Drops one component's handler without unsubscribing the channel, which would
  // also drop every sibling component bound to the same channel and force Pusher
  // to re-authorize it on the next subscribe.
  unbindEvent(channelName: string, event: string, callback: Function) {
    this.dropPendingBind(channelName, event, callback)
    this.pusher.channel(channelName)?.unbind(event, callback)
  }

  private hasPendingBind(channelName: string, event: string, callback: Function) {
    return this.pendingBinds.some(
      (bind) => bind.channelName === channelName && bind.event === event && bind.callback === callback
    )
  }

  private dropPendingBind(channelName: string, event: string, callback: Function) {
    remove(
      this.pendingBinds,
      (bind) => bind.channelName === channelName && bind.event === event && bind.callback === callback
    )
  }

  private init() {
    this.maybeEnableLogging()

    this.pusher = new Pusher(this.appKey, {
      cluster: this.cluster,
      channelAuthorization: {
        endpoint: this.channelEndpoint,
        transport: 'ajax'
      }
    })
  }

  private maybeEnableLogging() {
    if (this.environment === 'development' || this.environment === 'staging') {
      Pusher.log = (message) => {
        if (window.console && window.console.log) {
          window.console.log(message)
        }
      }
    }
  }
}

const instances = new Map<string, PusherWrapper>()

export const getPusherWrapper = (appKey: string, cluster: string): PusherWrapper => {
  const key = `${appKey}|${cluster}`
  let instance = instances.get(key)

  if (!instance) {
    instance = new PusherWrapper(appKey, cluster)
    instances.set(key, instance)
  }

  return instance
}
